import {
  createContext,
  type PropsWithChildren,
  useContext,
  useState,
} from 'react';
import { getMonthToDate } from '@witmetrics/utils/time';
import type { DateRange } from '@witmetrics/utils';
import { useTimeframe } from '@/hooks/useTimeframe';
import type { Reports } from './utils';

type ReportsValue = {
  reports: Reports;
  timeframe: DateRange;
  comparison: DateRange | null;
  onUpdateTimeframe: (update: DateRange) => void;
  onUpdateComparison: (update: DateRange | null) => void;
  onAddReports: (reports: Partial<Reports>) => void;
};

const ReportsContext = createContext<ReportsValue>({
  reports: {},
  timeframe: getMonthToDate(),
  comparison: null,
  onUpdateTimeframe: () => null,
  onUpdateComparison: () => null,
  onAddReports: () => null,
});

export default function ReportsProvider({ children }: PropsWithChildren) {
  const [timeframe, setTimeframe] = useTimeframe(getMonthToDate());
  const [comparison, setComparison] = useState<DateRange | null>(null);
  const [reports, setReports] = useState<Reports>({});

  const handleAddReports = (newReports: Partial<Reports> = {}) => {
    // NOTE: object key should be report name
    // @ts-ignore TODO
    setReports((reports) => ({ ...reports, ...newReports }));
  };

  const contextValue = {
    reports,
    timeframe,
    comparison,
    onUpdateTimeframe: (update: DateRange) => setTimeframe(update),
    onUpdateComparison: (update: DateRange | null) => setComparison(update),
    onAddReports: handleAddReports,
  };

  return (
    <ReportsContext.Provider value={contextValue}>
      {children}
    </ReportsContext.Provider>
  );
}

export function useReports() {
  const context = useContext(ReportsContext);
  if (!context) {
    throw Error('useReports must be used within the ReportsProvider');
  }
  return context;
}
